import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import ContactProfile from './ContactProfile';
import apiService from '../services/api';

const ContactProfilePage = ({ selectedLocation }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadContact();
  }, [id]);

  const loadContact = async () => {
    if (!id) {
      setError('No contact ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contactData = await apiService.getCRMContact(id);
      setContact(contactData);
    } catch (error) {
      console.error('Error loading contact:', error);
      setError('Contact not found');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/crm/contacts');
  };

  const handleUpdate = (updatedContact) => {
    setContact(updatedContact);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading contact...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Contact not found</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contacts
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Contact Profile</h1>
            <p className="text-sm text-gray-600">View and manage contact information</p>
          </div>
        </div>
      </div>

      {/* Contact Profile Content */}
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <ContactProfile 
            contact={contact}
            onClose={handleBack}
            onUpdate={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactProfilePage;